import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import Card from "../../components/Common/Card";
import Button from "../../components/Common/Button";
import Input from "../../components/Common/Input";
import Select from "../../components/Common/Select";
import Textarea from "../../components/Common/Textarea";
import MultiFileUpload from "../../components/Common/MultiFileUpload";
import Modal from "../../components/Common/Modal";
import SaleForm from "../../components/Sales/SaleForm";
import DownPaymentForm from "../../components/DownPayments/DownPaymentForm";
import RentalForm from "../../components/Rentals/RentalForm";
import InterestedCustomers from "../../components/Portfolios/InterestedCustomers";
import api from "../../utils/api";
import { showSuccess, showError, showDeleteConfirm } from "../../utils/swal";
import {
  ArrowLeft,
  Edit,
  MapPin,
  Home,
  Maximize,
  DollarSign,
  Building2,
  Layers,
  Compass,
  Save,
  X,
  Share2,
  Copy,
  Lock,
  Unlock,
  FileEdit,
  Briefcase,
  Trash2,
  User,
} from "lucide-react";
import { usePermission } from "../../hooks/usePermission";

const PortfolioDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, isPortfolioFullAccess } = useAuth();
  const { canEdit, canDelete, canShare, canChangeStatus } = usePermission();

  const canUserModify = Boolean(
    portfolio && canEdit('portfolios') && (
      portfolio.user_id === user?.id ||
      user?.is_manager ||
      user?.role === 'office_owner' ||
      isPortfolioFullAccess()
    ) && (!portfolio.sales || portfolio.sales.length === 0) && (!portfolio.rentals || portfolio.rentals.length === 0)
  );

  const canUserCancelSale = Boolean(
    portfolio && canDelete('portfolios') && (
      portfolio.user_id === user?.id ||
      user?.is_manager ||
      user?.role === 'office_owner' ||
      isPortfolioFullAccess()
    )
  );

  const canUserDelete = Boolean(
    portfolio && canDelete('portfolios') && (
      portfolio.user_id === user?.id ||
      user?.is_manager ||
      user?.role === 'office_owner' ||
      isPortfolioFullAccess()
    )
  );

  // Dinamik opsiyonlar
  const [options, setOptions] = useState({
    property_types: [],
    residential_types: [],
    listing_types: [],
    sale_types: [],
    portfolio_statuses: [],
    room_definitions: [],
    facade_types: [],
    cities: [],
  });
  const [districts, setDistricts] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);

  // Inline editing states
  const [editingGeneral, setEditingGeneral] = useState(false);
  const [generalForm, setGeneralForm] = useState({});

  const [editingPrice, setEditingPrice] = useState(false);
  const [priceForm, setPriceForm] = useState({});

  const [editingLocation, setEditingLocation] = useState(false);
  const [locationForm, setLocationForm] = useState({});

  // Image upload states
  const [uploadingImages, setUploadingImages] = useState(false);

  // Sale and Down Payment modals
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [showOptionModal, setShowOptionModal] = useState(false);
  const [showDownPaymentModal, setShowDownPaymentModal] = useState(false);
  const [showRentalModal, setShowRentalModal] = useState(false);

  // Option form states
  const [optionType, setOptionType] = useState('customer');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [managerName, setManagerName] = useState('');
  const [optionNotes, setOptionNotes] = useState('');
  const [customers, setCustomers] = useState([]);

  // Public Share states
  const [showShareModal, setShowShareModal] = useState(false);
  const [publicShareLink, setPublicShareLink] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [shareStatus, setShareStatus] = useState(null);

  const activeDownPayment = portfolio?.down_payments?.find(dp => !dp.is_converted_to_sale);
  const activeSale = portfolio?.sales && portfolio.sales.length > 0 ? portfolio.sales[0] : null;
  const activeRental = portfolio?.rentals && portfolio.rentals.length > 0 ? portfolio.rentals[0] : null;

  useEffect(() => {
    fetchOptions();
    fetchCustomers();
    fetchPortfolio();
  }, [id]);

  const fetchShareStatusAndLink = async (currentPortfolio) => {
    const isOwner = currentPortfolio.user_id === user?.id || user?.is_manager || user?.role === 'office_owner';
    try {
      if (isOwner) {
        const resp = await api.get(`/portfolios/${id}/public-share`);
        if (resp.data.success && resp.data.url) {
          setPublicShareLink(resp.data.url);
        } else {
          setPublicShareLink('');
        }
      } else {
        const resp = await api.get(`/portfolios/${id}/share-status`);
        if (resp.data.success) {
          setShareStatus(resp.data.status);
          if (resp.data.url) {
            setPublicShareLink(resp.data.url);
          }
        }
      }
    } catch (e) {
      console.error('Share link/status error', e);
    }
  };

  const fetchOptions = async () => {
    try {
      const response = await api.get('/portfolio-options');
      setOptions(response.data);
    } catch (error) {
      console.error('Opsiyonlar yüklenemedi:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data.data || response.data);
    } catch (error) {
      console.error('Müşteriler yüklenemedi:', error);
    }
  };

  const handleRemoveDownPayment = async (downPaymentId) => {
    const result = await showDeleteConfirm('Bu kapora işlemini iptal etmek istediğinize emin misiniz?');
    if (!result.isConfirmed) return;

    try {
      await api.delete(`/down-payments/${downPaymentId}`);
      showSuccess('Kapora başarıyla kaldırıldı');
      fetchPortfolio();
    } catch (error) {
      console.error('Kapora kaldırılamadı:', error);
      showError(error.response?.data?.message || 'Kapora kaldırılırken bir hata oluştu');
    }
  };

  const fetchPortfolio = async () => {
    try {
      const response = await api.get(`/portfolios/${id}`);
      setPortfolio(response.data);

      // Konum verileri için ilçe ve mahalle yükle
      if (response.data.city_id) {
        fetchDistricts(response.data.city_id);
      }
      if (response.data.district_id) {
        fetchNeighborhoods(response.data.district_id);
      }

      await fetchShareStatusAndLink(response.data);

      setLoading(false);
    } catch (error) {
      console.error("Portföy yüklenemedi:", error);
      setLoading(false);
    }
  };

  const fetchDistricts = async (cityId) => {
    try {
      const response = await api.get('/districts', { params: { city_id: cityId } });
      setDistricts(response.data);
    } catch (error) {
      console.error('İlçeler yüklenemedi:', error);
    }
  };

  const fetchNeighborhoods = async (districtId) => {
    try {
      const response = await api.get('/neighborhoods', { params: { district_id: districtId } });
      setNeighborhoods(response.data);
    } catch (error) {
      console.error('Mahalleler yüklenemedi:', error);
    }
  };

  // General info handlers
  const handleEditGeneral = () => {
    setGeneralForm({
      property_type_id: portfolio.property_type_id || "",
      residential_type_id: portfolio.residential_type_id || "",
      listing_type_id: portfolio.listing_type_id || "",
      sale_type_id: portfolio.sale_type_id || "",
      status_id: portfolio.status_id || "",
      room_definition_id: portfolio.room_definition_id || "",
      facade_type_id: portfolio.facade_type_id || "",
      building_age: portfolio.building_age || "",
      block: portfolio.block || "",
      flat_number: portfolio.flat_number || "",
      gross_sqm: portfolio.gross_sqm || "",
      net_sqm: portfolio.net_sqm || "",
      floor: portfolio.floor || "",
    });
    setEditingGeneral(true);
  };

  const handleSaveGeneral = async () => {
    try {
      const dataToSend = {
        ...generalForm,
        building_age: generalForm.building_age ? parseInt(generalForm.building_age) : null,
        gross_sqm: generalForm.gross_sqm ? parseFloat(generalForm.gross_sqm) : null,
        net_sqm: generalForm.net_sqm ? parseFloat(generalForm.net_sqm) : null,
      };

      await api.put(`/portfolios/${id}`, dataToSend);
      await fetchPortfolio();
      setEditingGeneral(false);
    } catch (error) {
      console.error("Genel bilgiler güncellenemedi:", error);
      const errorMessage = error.response?.data?.message
        || JSON.stringify(error.response?.data?.errors, null, 2)
        || error.message;
      showError(errorMessage, 'Genel Bilgiler Güncellenemedi');
    }
  };

  // Price info handlers
  const handleEditPrice = () => {
    setPriceForm({
      initial_price: portfolio.initial_price || "",
      list_price: portfolio.list_price || "",
      commission_rate: portfolio.commission_rate || "",
    });
    setEditingPrice(true);
  };

  const handleSavePrice = async () => {
    try {
      const dataToSend = {
        initial_price: priceForm.initial_price ? parseFloat(priceForm.initial_price) : null,
        list_price: priceForm.list_price ? parseFloat(priceForm.list_price) : null,
        commission_rate: priceForm.commission_rate ? parseFloat(priceForm.commission_rate) : null,
      };

      await api.put(`/portfolios/${id}`, dataToSend);
      await fetchPortfolio();
      setEditingPrice(false);
    } catch (error) {
      console.error("Fiyat bilgileri güncellenemedi:", error);
      const errorMessage = error.response?.data?.message
        || JSON.stringify(error.response?.data?.errors, null, 2)
        || error.message;
      showError(errorMessage, 'Fiyat Bilgileri Güncellenemedi');
    }
  };

  // Location info handlers
  const handleEditLocation = () => {
    setLocationForm({
      city_id: portfolio.city_id || "",
      district_id: portfolio.district_id || "",
      neighborhood_id: portfolio.neighborhood_id || "",
      address_detail: portfolio.address_detail || "",
      cadastral_info: portfolio.cadastral_info || "",
    });

    if (portfolio.city_id) {
      fetchDistricts(portfolio.city_id);
    }
    if (portfolio.district_id) {
      fetchNeighborhoods(portfolio.district_id);
    }

    setEditingLocation(true);
  };

  const handleLocationChange = (e) => {
    const { name, value } = e.target;

    if (name === 'city_id') {
      setLocationForm(prev => ({
        ...prev,
        city_id: value,
        district_id: '',
        neighborhood_id: '',
      }));
      if (value) {
        fetchDistricts(value);
      } else {
        setDistricts([]);
      }
      setNeighborhoods([]);
      return;
    }

    if (name === 'district_id') {
      setLocationForm(prev => ({
        ...prev,
        district_id: value,
        neighborhood_id: '',
      }));
      if (value) {
        fetchNeighborhoods(value);
      } else {
        setNeighborhoods([]);
      }
      return;
    }

    setLocationForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveLocation = async () => {
    try {
      await api.put(`/portfolios/${id}`, locationForm);
      await fetchPortfolio();
      setEditingLocation(false);
    } catch (error) {
      console.error("Konum bilgileri güncellenemedi:", error);
      const errorMessage = error.response?.data?.message
        || JSON.stringify(error.response?.data?.errors)
        || error.message;
      showError(errorMessage, 'Konum Bilgileri Güncellenemedi');
    }
  };

  // Image upload handlers
  const handleImageUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      // 1. Upload the file
      const uploadResp = await api.post(
        `/portfolios/${id}/upload-images`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (!uploadResp.data.success) {
        throw new Error(uploadResp.data.message || "Yükleme başarısız");
      }

      const newFile = uploadResp.data.files[0];
      const newPath = newFile.path;
      const newUrl = newFile.url;

      // 2. Get latest state to avoid race conditions
      const latestResp = await api.get(`/portfolios/${id}`);
      const latestImages = latestResp.data.images || [];

      // 3. Update the record
      await api.put(`/portfolios/${id}`, {
        images: [...latestImages, newPath],
      });

      fetchPortfolio();
      return { url: newUrl, path: newPath };
    } catch (error) {
      console.error("Portfolio image upload error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.errors ||
        error.message ||
        "Görsel yüklenirken bir hata oluştu";
      throw new Error(errorMessage);
    }
  };

  const handleDeleteImage = async (index) => {
    const result = await showDeleteConfirm('Bu görsel');
    if (!result.isConfirmed) return;

    try {
      const images = [...(portfolio.images || [])];
      images.splice(index, 1);
      await api.put(`/portfolios/${id}`, { images });
      fetchPortfolio();
      showSuccess('Görsel başarıyla silindi!');
    } catch (error) {
      console.error('Görsel silinemedi:', error);
      showError('Görsel silinirken bir hata oluştu', 'Silme Hatası');
    }
  };

  const handleOptionSubmit = async (e) => {
    e.preventDefault();

    if (optionType === 'customer' && !selectedCustomerId) {
      showError('Lütfen bir müşteri seçiniz');
      return;
    }
    if (optionType === 'manager' && !managerName.trim()) {
      showError('Lütfen yönetici adını giriniz');
      return;
    }

    try {
      const payload = {
        option_type: optionType,
        customer_id: optionType === 'customer' ? selectedCustomerId : null,
        manager_name: optionType === 'manager' ? managerName : null,
        notes: optionNotes,
      };

      await api.post(`/portfolios/${id}/set-option`, payload);
      showSuccess('Portföy opsiyonlandı');
      setShowOptionModal(false);
      setOptionType('customer');
      setSelectedCustomerId('');
      setManagerName('');
      setOptionNotes('');
      fetchPortfolio();
    } catch (error) {
      console.error('Opsiyon eklenemedi:', error);
      showError(error.response?.data?.message || 'Opsiyon eklenirken bir hata oluştu');
    }
  };

  const handleEnableShare = async () => {
    if (!canUserModify || !canShare('portfolios')) return;
    setShareLoading(true);
    try {
      const resp = await api.post(`/portfolios/${id}/public-share`);
      if (resp.data.success) {
        setPublicShareLink(resp.data.url);
        showSuccess("Public link oluşturuldu");
      }
    } catch (e) {
      showError(e.response?.data?.message || "Link oluşturulamadı. (Yetkiniz olmayabilir)");
    } finally {
      setShareLoading(false);
    }
  };

  const handleRequestShare = async () => {
    if (!canShare('portfolios')) return;
    setShareLoading(true);
    try {
      const resp = await api.post(`/portfolios/${id}/request-share`);
      if (resp.data.success) {
        setShareStatus(resp.data.status);
        showSuccess(resp.data.message || "Paylaşım talebiniz iletildi");
      }
    } catch (e) {
      showError(e.response?.data?.message || "Talep oluşturulamadı");
    } finally {
      setShareLoading(false);
    }
  };

  const handleDisableShare = async () => {
    setShareLoading(true);
    try {
      const resp = await api.delete(`/portfolios/${id}/public-share`);
      if (resp.data.success) {
        setPublicShareLink('');
        showSuccess("Public link kaldırıldı");
      }
    } catch (e) {
      showError(e.response?.data?.message || "Link kaldırılamadı");
    } finally {
      setShareLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicShareLink);
    showSuccess("Link kopyalandı!");
  };

  if (loading) {
    return <div className="text-center py-8">Yükleniyor...</div>;
  }

  if (!portfolio) {
    return <div className="text-center py-8">Portföy bulunamadı</div>;
  }

  const getStatusColor = (statusCode) => {
    switch (statusCode) {
      case "active":
        return "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800";
      case "deposit":
        return "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800";
      case "option":
        return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800";
      case "manager_option":
        return "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800";
      case "sold":
        return "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800";
      case "rented":
        return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600";
    }
  };

  // Helper: ilişki üzerinden isim al
  const getRelationName = (relation) => relation?.name || "-";

  // Code kontrolleri
  const statusCode = portfolio.status?.code;
  const listingTypeCode = portfolio.listing_type?.code;
  const propertyTypeCode = portfolio.property_type?.code;

  // Editing formundaki code kontrolleri
  const editPropertyType = options.property_types.find(t => t.id == generalForm.property_type_id);

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate("/portfolios")}
        >
          <ArrowLeft size={16} />
          Geri
        </Button>
        <div className="flex gap-2">
          {canShare('portfolios') && portfolio.visibility !== 'draft' && (
            <Button
              size="sm"
              variant="outline"
              className="border-gray-300 text-gray-700 bg-white"
              onClick={() => setShowShareModal(true)}
            >
              <Share2 size={16} className="mr-1" />
              {(portfolio.visibility === 'shared' || portfolio.user_id === user?.id || user?.is_manager || user?.role === 'office_owner') ? "Portföyü Paylaş" : "Paylaşım Talep Et"}
            </Button>
          )}
          {portfolio.visibility === 'draft' && (
            <span className="inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
              <FileEdit size={14} className="mr-1" />
              Taslak — Paylaşılamaz
            </span>
          )}

          {canUserModify && (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setShowDownPaymentModal(true)}
                disabled={statusCode === 'sold' || statusCode === 'rented' || statusCode === 'deposit' || !canChangeStatus('portfolios')}
              >
                Kapora Al
              </Button>
              <Button
                size="sm"
                variant="warning"
                onClick={() => setShowOptionModal(true)}
                disabled={statusCode === 'option' || statusCode === 'sold' || statusCode === 'rented' || !canChangeStatus('portfolios')}
              >
                Opsiyonla
              </Button>
              {listingTypeCode === 'sale' && (
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => setShowSaleModal(true)}
                  disabled={statusCode === 'sold' || statusCode === 'rented' || !canChangeStatus('portfolios')}
                >
                  Satış Yap
                </Button>
              )}
              {listingTypeCode === 'rental' && (
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => setShowRentalModal(true)}
                  disabled={statusCode === 'sold' || statusCode === 'rented' || !canChangeStatus('portfolios')}
                >
                  Kirala
                </Button>
              )}
              <Button size="sm" onClick={() => navigate(`/portfolios/${id}/edit`)}>
                <Edit size={16} />
                Düzenle
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Header */}
      <Card className="mb-6">
        <div className="flex items-start justify-between gap-6">
          {portfolio.cover_photo_url && (
            <div className="w-48 h-32 flex-shrink-0">
              <img
                src={portfolio.cover_photo_url}
                alt={portfolio.title}
                className="w-full h-full object-cover rounded-lg border border-gray-200"
              />
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <h1 className="text-3xl font-bold text-gray-900 pr-3 border-r border-gray-200">
                {portfolio.title}
              </h1>
              <div className="flex gap-2">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(statusCode)}`}
                >
                  {getRelationName(portfolio.status)}
                </span>
                {portfolio.visibility === 'shared' && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800">
                    <Unlock size={12} className="mr-1" />
                    Paylaşımlı
                  </span>
                )}
                {portfolio.visibility === 'private' && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800">
                    <Lock size={12} className="mr-1" />
                    Özel
                  </span>
                )}
                {portfolio.visibility === 'draft' && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600">
                    <FileEdit size={12} className="mr-1" />
                    Taslak
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-4 text-gray-600">
              <div className="flex items-center gap-2">
                <User size={18} />
                <span>
                  {portfolio.user?.name || "-"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={18} />
                <span>
                  {getRelationName(portfolio.city)}, {getRelationName(portfolio.district)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Home size={18} />
                <span>{getRelationName(portfolio.property_type)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Building2 size={18} />
                <span>
                  {getRelationName(portfolio.listing_type)} - {getRelationName(portfolio.sale_type)}
                </span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500 mb-1">Liste Fiyatı</div>
            <div className="text-3xl font-bold text-primary-600">
              {portfolio.list_price?.toLocaleString("tr-TR")} ₺
            </div>
            {portfolio.initial_price &&
              portfolio.initial_price !== portfolio.list_price && (
                <div className="text-sm text-gray-500 line-through">
                  {portfolio.initial_price?.toLocaleString("tr-TR")} ₺
                </div>
              )}
          </div>
        </div>
      </Card>

      {/* Satış Bilgileri */}
      {activeSale && (
        <Card className="mb-6 border-2 border-green-400 bg-green-50 dark:bg-green-900/10 dark:border-green-900/50">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-green-800 dark:text-green-400 mb-3">
                ✅ Satılmış Portföy
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-green-600 mb-1">Müşteri</p>
                  <p className="font-semibold text-gray-900">
                    <span
                      className="text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer"
                      onClick={() => navigate(`/customers/${activeSale.customer?.id}`)}
                    >
                      {activeSale.customer?.name} {activeSale.customer?.surname}
                    </span>
                  </p>
                </div>
                <div>
                  <p className="text-sm text-green-600 mb-1">Satış Fiyatı</p>
                  <p className="font-bold text-green-700">
                    {parseFloat(activeSale.final_sale_price).toLocaleString('tr-TR')} ₺
                  </p>
                </div>
                <div>
                  <p className="text-sm text-green-600 mb-1">Komisyon</p>
                  <p className="font-semibold text-gray-900">
                    {activeSale.commission_amount ? `${parseFloat(activeSale.commission_amount).toLocaleString('tr-TR')} ₺` : '-'}
                    {activeSale.commission_rate ? ` (%${activeSale.commission_rate})` : ''}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-green-600 mb-1">Satış Tarihi</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(activeSale.sale_date).toLocaleDateString('tr-TR', {
                      year: 'numeric', month: 'long', day: 'numeric'
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-green-600 mb-1">İşlemi Yapan</p>
                  <p className="font-semibold text-gray-900">
                    {activeSale.user?.name || '-'}
                  </p>
                </div>
                {activeSale.purchase_motive && (
                  <div>
                    <p className="text-sm text-green-600 mb-1">Alım Amacı</p>
                    <p className="font-semibold text-gray-900">
                      {activeSale.purchase_motive === 'investment' ? 'Yatırım' :
                        activeSale.purchase_motive === 'residence' ? 'Oturum' :
                          activeSale.purchase_motive === 'investment_and_residence' ? 'Yatırım ve Oturum' : activeSale.purchase_motive}
                    </p>
                  </div>
                )}
                {activeSale.sale_notes && (
                  <div className="md:col-span-2 lg:col-span-4">
                    <p className="text-sm text-green-600 mb-1">Notlar</p>
                    <p className="text-gray-900 whitespace-pre-wrap">{activeSale.sale_notes}</p>
                  </div>
                )}
              </div>
            </div>
            {canUserCancelSale && (
              <Button
                size="sm"
                variant="danger"
                onClick={async () => {
                  const result = await showDeleteConfirm('Bu satış işlemini iptal etmek istediğinize emin misiniz?');
                  if (!result.isConfirmed) return;
                  try {
                    await api.delete(`/sales/${activeSale.id}`);
                    showSuccess('Satış iptal edildi');
                    fetchPortfolio();
                  } catch (error) {
                    showError('Satış iptal edilemedi');
                  }
                }}
                disabled={!canChangeStatus('portfolios')}
              >
                <Trash2 size={16} />
                Satışı İptal Et
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Kiralama Bilgileri */}
      {activeRental && (
        <Card className="mb-6 border-2 border-blue-400 bg-blue-50 dark:bg-blue-900/10 dark:border-blue-900/50">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-400 mb-3">
                🏠 Kiralanmış Portföy
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-blue-600 mb-1">Müşteri</p>
                  <p className="font-semibold text-gray-900">
                    <span
                      className="text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer"
                      onClick={() => navigate(`/customers/${activeRental.customer?.id}`)}
                    >
                      {activeRental.customer?.name} {activeRental.customer?.surname}
                    </span>
                  </p>
                </div>
                <div>
                  <p className="text-sm text-blue-600 mb-1">Kira Bedeli</p>
                  <p className="font-bold text-blue-700">
                    {parseFloat(activeRental.rental_price).toLocaleString('tr-TR')} ₺
                  </p>
                </div>
                <div>
                  <p className="text-sm text-blue-600 mb-1">Komisyon</p>
                  <p className="font-semibold text-gray-900">
                    {activeRental.commission_amount ? `${parseFloat(activeRental.commission_amount).toLocaleString('tr-TR')} ₺` : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-blue-600 mb-1">Başlangıç Tarihi</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(activeRental.start_date).toLocaleDateString('tr-TR', {
                      year: 'numeric', month: 'long', day: 'numeric'
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-blue-600 mb-1">İşlemi Yapan</p>
                  <p className="font-semibold text-gray-900">
                    {activeRental.user?.name || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-blue-600 mb-1">Ödeme Periyodu</p>
                  <p className="font-semibold text-gray-900">
                    {activeRental.payment_period === 'monthly' ? 'Aylık' :
                      activeRental.payment_period === 'quarterly' ? '3 Aylık' :
                        activeRental.payment_period === 'semi_annual' ? '6 Aylık' :
                          activeRental.payment_period === 'annual' ? 'Yıllık' : activeRental.payment_period}
                  </p>
                </div>
                {activeRental.notes && (
                  <div className="md:col-span-2 lg:col-span-4">
                    <p className="text-sm text-blue-600 mb-1">Notlar</p>
                    <p className="text-gray-900 whitespace-pre-wrap">{activeRental.notes}</p>
                  </div>
                )}
              </div>
            </div>
            {canUserCancelSale && (
              <Button
                size="sm"
                variant="danger"
                onClick={async () => {
                  const result = await showDeleteConfirm('Bu kiralama işlemini iptal etmek istediğinize emin misiniz?');
                  if (!result.isConfirmed) return;
                  try {
                    await api.delete(`/rentals/${activeRental.id}`);
                    showSuccess('Kiralama iptal edildi');
                    fetchPortfolio();
                  } catch (error) {
                    showError('Kiralama iptal edilemedi');
                  }
                }}
                disabled={!canChangeStatus('portfolios')}
              >
                <Trash2 size={16} />
                Kiralamayı İptal Et
              </Button>
            )}
          </div>
        </Card>
      )}


      {/* Opsiyon Bilgileri */}
      {portfolio.option_type && (
        <Card className="mb-6 border-2 border-yellow-400 bg-yellow-50 dark:bg-yellow-900/10 dark:border-yellow-900/50">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-400 mb-3">
                🔒 Opsiyonlu Portföy
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Opsiyon Tipi</p>
                  <p className="font-semibold text-gray-900">
                    {portfolio.option_type === 'customer' ? 'Müşteri Opsiyonlu' : 'Yönetici Opsiyonlu'}
                  </p>
                </div>

                {portfolio.option_type === 'customer' && portfolio.option_customer && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Müşteri</p>
                    <p className="font-semibold text-gray-900">
                      <span
                        className="text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer"
                        onClick={() => navigate(`/customers/${portfolio.option_customer.id}`)}
                      >
                        {portfolio.option_customer.name} {portfolio.option_customer.surname}
                      </span>
                    </p>
                  </div>
                )}

                {portfolio.option_type === 'manager' && portfolio.option_manager_name && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Yönetici</p>
                    <p className="font-semibold text-gray-900">
                      {portfolio.option_manager_name}
                    </p>
                  </div>
                )}

                {portfolio.option_date && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Opsiyon Tarihi</p>
                    <p className="font-semibold text-gray-900">
                      {new Date(portfolio.option_date).toLocaleDateString('tr-TR', {
                        year: 'numeric', month: 'long', day: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </p>
                  </div>
                )}

                {portfolio.option_notes && (
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-600 mb-1">Notlar</p>
                    <p className="text-gray-900 whitespace-pre-wrap">
                      {portfolio.option_notes}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <Button
              size="sm"
              variant="danger"
              onClick={async () => {
                const result = await showDeleteConfirm('Bu opsiyonu kaldırmak istediğinize emin misiniz?');
                if (!result.isConfirmed) return;
                try {
                  await api.delete(`/portfolios/${id}/remove-option`);
                  showSuccess('Opsiyon kaldırıldı');
                  fetchPortfolio();
                } catch (error) {
                  showError('Opsiyon kaldırılamadı');
                }
              }}
              disabled={!canChangeStatus('portfolios')}
            >
              <X size={16} />
              Opsiyonu Kaldır
            </Button>
          </div>
        </Card>
      )}

      {/* Kapora Bilgileri */}
      {activeDownPayment && (
        <Card className="mb-6 border-2 border-orange-400 bg-orange-50 dark:bg-orange-900/10 dark:border-orange-900/50">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-orange-800 dark:text-orange-400 mb-3">
                💰 Kaporalı Portföy
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-orange-600 mb-1">Müşteri</p>
                  <p className="font-semibold text-gray-900">
                    <span
                      className="text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer"
                      onClick={() => navigate(`/customers/${activeDownPayment.customer.id}`)}
                    >
                      {activeDownPayment.customer?.name} {activeDownPayment.customer?.surname}
                    </span>
                  </p>
                </div>

                <div>
                  <p className="text-sm text-orange-600 mb-1">Alınan Kapora</p>
                  <p className="font-bold text-orange-700">
                    {parseFloat(activeDownPayment.amount_received).toLocaleString('tr-TR')} ₺
                  </p>
                </div>

                <div>
                  <p className="text-sm text-orange-600 mb-1">Anlaşılan Fiyat</p>
                  <p className="font-semibold text-gray-900">
                    {parseFloat(activeDownPayment.agreed_price).toLocaleString('tr-TR')} ₺
                  </p>
                </div>

                <div>
                  <p className="text-sm text-orange-600 mb-1">Kapora Tarihi</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(activeDownPayment.date).toLocaleDateString('tr-TR', {
                      year: 'numeric', month: 'long', day: 'numeric'
                    })}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-orange-600 mb-1">İşlemi Yapan</p>
                  <p className="font-semibold text-gray-900">
                    {activeDownPayment.user?.name || '-'}
                  </p>
                </div>
              </div>
            </div>
            {canUserModify && (
              <Button
                size="sm"
                variant="danger"
                onClick={() => handleRemoveDownPayment(activeDownPayment.id)}
                disabled={!canChangeStatus('portfolios')}
              >
                <Trash2 size={16} />
                Kaporayı Kaldır
              </Button>
            )}
          </div>
        </Card>
      )}


      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Layers size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Oda Sayısı</p>
              <p className="text-lg font-bold text-gray-900">
                {getRelationName(portfolio.room_definition)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-green-100 p-2 rounded-lg">
              <Maximize size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Net m²</p>
              <p className="text-lg font-bold text-gray-900">
                {portfolio.net_sqm || "-"} m²
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <Building2 size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Kat</p>
              <p className="text-lg font-bold text-gray-900">
                {portfolio.floor || "-"}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-orange-100 p-2 rounded-lg">
              <Compass size={20} className="text-orange-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Cephe</p>
              <p className="text-lg font-bold text-gray-900">
                {getRelationName(portfolio.facade_type)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Genel Bilgiler */}
        <Card
          title="Genel Bilgiler"
          className="lg:col-span-2"
          action={
            canUserModify && (!editingGeneral ? (
              <button
                onClick={handleEditGeneral}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                title="Düzenle"
              >
                <Edit size={16} />
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => setEditingGeneral(false)}
                  className="px-3 py-1.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
                >
                  İptal
                </button>
                <button
                  onClick={handleSaveGeneral}
                  className="px-3 py-1.5 bg-primary-600 text-white hover:bg-primary-700 rounded-lg text-sm flex items-center gap-1"
                >
                  <Save size={14} />
                  Kaydet
                </button>
              </div>
            ))
          }
        >
          {!editingGeneral ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Emlak Tipi</p>
                <p className="font-semibold">{getRelationName(portfolio.property_type)}</p>
              </div>
              {propertyTypeCode === 'residential' && portfolio.residential_type && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Konut Tipi</p>
                  <p className="font-semibold">{getRelationName(portfolio.residential_type)}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500 mb-1">İlan Tipi</p>
                <p className="font-semibold">{getRelationName(portfolio.listing_type)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Satış Tipi</p>
                <p className="font-semibold">{getRelationName(portfolio.sale_type)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Bina Yaşı</p>
                <p className="font-semibold">
                  {portfolio.building_age || "Sıfır"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Blok</p>
                <p className="font-semibold">{portfolio.block || "-"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Daire No</p>
                <p className="font-semibold">{portfolio.flat_number || "-"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Brüt Metrekare</p>
                <p className="font-semibold">{portfolio.gross_sqm} m²</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Net Metrekare</p>
                <p className="font-semibold">{portfolio.net_sqm} m²</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Cephe</p>
                <p className="font-semibold">{getRelationName(portfolio.facade_type)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Kat</p>
                <p className="font-semibold">{portfolio.floor || "-"}</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="Emlak Tipi"
                name="property_type_id"
                value={generalForm.property_type_id}
                onChange={(e) =>
                  setGeneralForm({ ...generalForm, property_type_id: e.target.value })
                }
                options={options.property_types.map(t => ({ value: t.id, label: t.name }))}
              />
              {editPropertyType?.code === 'residential' && (
                <Select
                  label="Konut Tipi"
                  name="residential_type_id"
                  value={generalForm.residential_type_id || ''}
                  onChange={(e) =>
                    setGeneralForm({ ...generalForm, residential_type_id: e.target.value })
                  }
                  options={options.residential_types.map(t => ({ value: t.id, label: t.name }))}
                />
              )}
              <Select
                label="İlan Tipi"
                name="listing_type_id"
                value={generalForm.listing_type_id}
                onChange={(e) =>
                  setGeneralForm({ ...generalForm, listing_type_id: e.target.value })
                }
                options={options.listing_types.map(t => ({ value: t.id, label: t.name }))}
              />
              <Select
                label="Satış Tipi"
                name="sale_type_id"
                value={generalForm.sale_type_id}
                onChange={(e) =>
                  setGeneralForm({ ...generalForm, sale_type_id: e.target.value })
                }
                options={options.sale_types.map(t => ({ value: t.id, label: t.name }))}
              />
              <Select
                label="Durum"
                name="status_id"
                value={generalForm.status_id}
                onChange={(e) =>
                  setGeneralForm({ ...generalForm, status_id: e.target.value })
                }
                options={options.portfolio_statuses.map(t => ({ value: t.id, label: t.name }))}
              />
              <Select
                label="Oda Sayısı"
                name="room_definition_id"
                value={generalForm.room_definition_id}
                onChange={(e) =>
                  setGeneralForm({ ...generalForm, room_definition_id: e.target.value })
                }
                options={options.room_definitions.map(t => ({ value: t.id, label: t.name }))}
              />
              <Select
                label="Cephe"
                name="facade_type_id"
                value={generalForm.facade_type_id}
                onChange={(e) =>
                  setGeneralForm({ ...generalForm, facade_type_id: e.target.value })
                }
                options={options.facade_types.map(t => ({ value: t.id, label: t.name }))}
              />
              <Input
                label="Bina Yaşı"
                value={generalForm.building_age}
                onChange={(e) =>
                  setGeneralForm({ ...generalForm, building_age: e.target.value })
                }
              />
              <Input
                label="Blok"
                value={generalForm.block}
                onChange={(e) =>
                  setGeneralForm({ ...generalForm, block: e.target.value })
                }
              />
              <Input
                label="Daire No"
                value={generalForm.flat_number}
                onChange={(e) =>
                  setGeneralForm({ ...generalForm, flat_number: e.target.value })
                }
              />
              <Input
                label="Brüt Metrekare"
                type="number"
                value={generalForm.gross_sqm}
                onChange={(e) =>
                  setGeneralForm({ ...generalForm, gross_sqm: e.target.value })
                }
              />
              <Input
                label="Net Metrekare"
                type="number"
                value={generalForm.net_sqm}
                onChange={(e) =>
                  setGeneralForm({ ...generalForm, net_sqm: e.target.value })
                }
              />
              <Input
                label="Kat"
                value={generalForm.floor}
                onChange={(e) =>
                  setGeneralForm({ ...generalForm, floor: e.target.value })
                }
              />
            </div>
          )}
        </Card>

        {/* Fiyat Bilgileri */}
        <Card
          title="Fiyat Bilgileri"
          action={
            canUserModify && (!editingPrice ? (
              <button
                onClick={handleEditPrice}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                title="Düzenle"
              >
                <Edit size={16} />
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => setEditingPrice(false)}
                  className="px-3 py-1.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
                >
                  İptal
                </button>
                <button
                  onClick={handleSavePrice}
                  className="px-3 py-1.5 bg-primary-600 text-white hover:bg-primary-700 rounded-lg text-sm flex items-center gap-1"
                >
                  <Save size={14} />
                  Kaydet
                </button>
              </div>
            ))
          }
        >
          {!editingPrice ? (
            <div className="space-y-4">
              {portfolio.initial_price && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">İlk Fiyat</p>
                  <p className="text-lg font-semibold">
                    {portfolio.initial_price?.toLocaleString("tr-TR")} ₺
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500 mb-1">Liste Fiyatı</p>
                <p className="text-xl font-bold text-primary-600">
                  {portfolio.list_price?.toLocaleString("tr-TR")} ₺
                </p>
              </div>
              {portfolio.commission_rate && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Komisyon Oranı</p>
                  <p className="font-semibold">%{portfolio.commission_rate}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <Input
                label="İlk Fiyat"
                type="number"
                value={priceForm.initial_price}
                onChange={(e) =>
                  setPriceForm({ ...priceForm, initial_price: e.target.value })
                }
              />
              <Input
                label="Liste Fiyatı"
                type="number"
                value={priceForm.list_price}
                onChange={(e) =>
                  setPriceForm({ ...priceForm, list_price: e.target.value })
                }
              />
              <Input
                label="Komisyon Oranı (%)"
                type="number"
                value={priceForm.commission_rate}
                onChange={(e) =>
                  setPriceForm({
                    ...priceForm,
                    commission_rate: e.target.value,
                  })
                }
              />
            </div>
          )}
        </Card>
      </div>

      {/* Konum Bilgileri */}
      <Card
        title="Konum Bilgileri"
        className="mb-6"
        action={
          canUserModify && (!editingLocation ? (
            <button
              onClick={handleEditLocation}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
              title="Düzenle"
            >
              <Edit size={16} />
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => setEditingLocation(false)}
                className="px-3 py-1.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
              >
                İptal
              </button>
              <button
                onClick={handleSaveLocation}
                className="px-3 py-1.5 bg-primary-600 text-white hover:bg-primary-700 rounded-lg text-sm flex items-center gap-1"
              >
                <Save size={14} />
                Kaydet
              </button>
            </div>
          ))
        }
      >
        {!editingLocation ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">İl</p>
                <p className="font-semibold">
                  {getRelationName(portfolio.city)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">İlçe</p>
                <p className="font-semibold">
                  {getRelationName(portfolio.district)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Mahalle</p>
                <p className="font-semibold">
                  {getRelationName(portfolio.neighborhood)}
                </p>
              </div>
            </div>
            {portfolio.address_detail && (
              <div className="mb-4">
                <p className="text-sm text-gray-500 mb-1">Adres Detayı</p>
                <p className="text-gray-700">{portfolio.address_detail}</p>
              </div>
            )}
            {portfolio.cadastral_info && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Kadastro Bilgisi</p>
                <p className="text-gray-700">{portfolio.cadastral_info}</p>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Select
                label="İl"
                name="city_id"
                value={locationForm.city_id}
                onChange={handleLocationChange}
                options={options.cities.map(c => ({ value: c.id, label: c.name }))}
              />
              <Select
                label="İlçe"
                name="district_id"
                value={locationForm.district_id}
                onChange={handleLocationChange}
                options={districts.map(d => ({ value: d.id, label: d.name }))}
              />
              <Select
                label="Mahalle"
                name="neighborhood_id"
                value={locationForm.neighborhood_id}
                onChange={handleLocationChange}
                options={neighborhoods.map(n => ({ value: n.id, label: n.name }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adres Detayı
              </label>
              <textarea
                value={locationForm.address_detail}
                onChange={(e) =>
                  setLocationForm({
                    ...locationForm,
                    address_detail: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                rows={2}
                placeholder="Adres detayı..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kadastro Bilgisi
              </label>
              <textarea
                value={locationForm.cadastral_info}
                onChange={(e) =>
                  setLocationForm({
                    ...locationForm,
                    cadastral_info: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                rows={2}
                placeholder="Kadastro bilgisi..."
              />
            </div>
          </div>
        )}
      </Card>

      {/* Proje Bilgileri */}
      {portfolio.project && (
        <Card title="Proje Bilgileri" className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold mb-1">
                {portfolio.project.name}
              </p>
              <p className="text-sm text-gray-600">
                {portfolio.project.location}
              </p>
              {portfolio.project_block && (
                <p className="text-sm text-gray-600 mt-1">
                  Blok: {portfolio.project_block.name}
                </p>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/projects/${portfolio.project.id}`)}
            >
              Projeyi Görüntüle
            </Button>
          </div>
        </Card>
      )}

      {/* Ek Bilgiler */}
      {portfolio.additional_info && (
        <Card title="Ek Bilgiler" className="mb-6">
          <div
            className="text-gray-700 overflow-x-auto"
            dangerouslySetInnerHTML={{ __html: portfolio.additional_info }}
          />
        </Card>
      )}

      {/* Görseller */}
      <Card title="Portföy Görselleri" className="mb-6">
        <MultiFileUpload
          onUpload={handleImageUpload}
          currentFiles={portfolio.image_urls ? portfolio.image_urls.map((url, index) => ({
            url: url,
            name: portfolio.images?.[index]?.split('/').pop() || `Görsel ${index + 1}`,
            preview: url
          })) : []}
          onDelete={handleDeleteImage}
          label=""
          description="WEBP, JPG, PNG (Max 10MB, Max 20 dosya)"
          accept="image/*"
          maxFiles={35}
        />
      </Card>

      {/* Satış Modal */}
      <Modal
        isOpen={showSaleModal}
        onClose={() => setShowSaleModal(false)}
        title="Satış Gerçekleştir"
      >
        <SaleForm
          portfolio={portfolio}
          onClose={() => setShowSaleModal(false)}
          onSuccess={() => {
            fetchPortfolio();
            setShowSaleModal(false);
          }}
        />
      </Modal>

      {/* Kapora Modal */}
      <Modal
        isOpen={showDownPaymentModal}
        onClose={() => setShowDownPaymentModal(false)}
        title="Kapora Al"
      >
        <DownPaymentForm
          portfolio={portfolio}
          onClose={() => setShowDownPaymentModal(false)}
          onSuccess={() => {
            fetchPortfolio();
            setShowDownPaymentModal(false);
          }}
        />
      </Modal>

      {/* Opsiyon Modal */}
      <Modal
        isOpen={showOptionModal}
        onClose={() => setShowOptionModal(false)}
        title="Portföy Opsiyonla"
      >
        <form onSubmit={handleOptionSubmit} className="space-y-4">
          <Select
            label="Opsiyon Tipi *"
            value={optionType}
            onChange={(e) => setOptionType(e.target.value)}
            options={[
              { value: 'customer', label: 'Müşteri Opsiyonlu' },
              { value: 'manager', label: 'Yönetici Opsiyonlu' },
            ]}
          />

          {optionType === 'customer' ? (
            <Select
              label="Müşteri Seçiniz *"
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
              required
              options={[
                { value: '', label: 'Müşteri seçiniz...' },
                ...customers.map(c => ({
                  value: c.id,
                  label: `${c.name} ${c.surname}${c.phone ? ' - ' + c.phone : ''}`
                }))
              ]}
            />
          ) : (
            <Input
              label="Yönetici Adı *"
              value={managerName}
              onChange={(e) => setManagerName(e.target.value)}
              placeholder="Yönetici adını giriniz"
              required
            />
          )}

          <Textarea
            label="Notlar"
            value={optionNotes}
            onChange={(e) => setOptionNotes(e.target.value)}
            rows={3}
            placeholder="Opsiyon ile ilgili notlar..."
          />

          <div className="flex gap-3 justify-end pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowOptionModal(false)}
            >
              İptal
            </Button>
            <Button type="submit">
              Opsiyonla
            </Button>
          </div>
        </form>
      </Modal>

      {/* Kiralama Modal */}
      <Modal
        isOpen={showRentalModal}
        onClose={() => setShowRentalModal(false)}
        title="Kiralama Yap"
      >
        <RentalForm
          portfolio={portfolio}
          onClose={() => setShowRentalModal(false)}
          onSuccess={() => {
            fetchPortfolio();
            setShowRentalModal(false);
          }}
        />
      </Modal>

      {/* İlgilenen Müşteriler */}
      <InterestedCustomers customers={portfolio.interested_customers} />

      {/* Paylaş Modal */}
      <Modal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title={canUserModify ? "Portföyü Paylaş" : "Paylaşım Talep Et"}
      >
        <div className="space-y-4">
          {canUserModify ? (
            <>
              <p className="text-sm text-gray-600">
                Bu portföyü müşterilerinizle paylaşmak için herkese açık bir bağlantı oluşturabilirsiniz. Bağlantıda hassas bilgiler (satıcı, iç not, vb.) gizlenecek ve tarafınıza ait olan iletişim bilgileriniz gösterilecektir.
              </p>

              {!publicShareLink ? (
                <div className="text-center py-4">
                  <Button onClick={handleEnableShare} disabled={shareLoading}>
                    {shareLoading ? 'Oluşturuluyor...' : 'Public Link Oluştur'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex gap-2 items-center">
                    <div className="flex-1">
                      <Input
                        value={publicShareLink}
                        readOnly
                        className="bg-gray-50 mb-0"
                        label="Paylaşım Linki"
                      />
                    </div>
                    <div className="pt-6">
                      <Button variant="secondary" onClick={handleCopyLink} title="Kopyala">
                        <Copy size={18} />
                      </Button>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-4 justify-between">
                    <Button variant="danger" onClick={handleDisableShare} disabled={shareLoading}>
                      Linki Kaldır
                    </Button>
                    <Button
                      variant="success"
                      onClick={() => window.open(`https://wa.me/?text=Bu portföyü inceleyebilirsiniz: ${publicShareLink}`, '_blank')}
                    >
                      WhatsApp ile Paylaş
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {portfolio.visibility === 'shared' ? (
                <div className="bg-blue-50 text-blue-800 p-4 rounded-lg border border-blue-200 mb-4 transition-all animate-in fade-in slide-in-from-top-1 duration-300">
                  <div className="flex items-center">
                    <Unlock size={18} className="mr-2" />
                    <p className="font-semibold">Ofise Açık Portföy</p>
                  </div>
                  <p className="text-sm mt-1">Bu portföy herkesin paylaşımına açılmıştır. Alt kısımdan kendinize özel paylaşım linkinizi oluşturabilirsiniz.</p>
                </div>
              ) : (
                <p className="text-sm text-gray-600 mb-4">
                  Bu portföy size ait değil. Müşterilerinizle paylaşmak ve kendi iletişim bilgilerinizi göstermek için portföy sahibinden veya yöneticiden paylaşım izni talep etmelisiniz.
                </p>
              )}

              {shareStatus === 'pending' && (
                <div className="bg-yellow-50 text-yellow-800 p-4 rounded-lg border border-yellow-200">
                  <p className="font-medium">Onay Bekleniyor</p>
                  <p className="text-sm mt-1">Paylaşım talebiniz portföy sahibine iletildi, henüz onaylanmadı.</p>
                </div>
              )}

              {shareStatus === 'approved' && publicShareLink && (
                <div className="space-y-4">
                  <div className="bg-green-50 text-green-800 p-3 rounded-lg border border-green-200 mb-4 text-sm font-medium">
                    Talebiniz onaylandı! Aşağıdaki adresi kullanarak portföyü kendi iletişim bilgilerinizle paylaşabilirsiniz.
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="flex-1">
                      <Input
                        value={publicShareLink}
                        readOnly
                        className="bg-gray-50 mb-0"
                        label="Size Özel Paylaşım Linki"
                      />
                    </div>
                    <div className="pt-6">
                      <Button variant="secondary" onClick={handleCopyLink} title="Kopyala">
                        <Copy size={18} />
                      </Button>
                    </div>
                  </div>
                  <div className="flex justify-end mt-4">
                    <Button
                      variant="success"
                      onClick={() => window.open(`https://wa.me/?text=Bu portföyü inceleyebilirsiniz: ${publicShareLink}`, '_blank')}
                    >
                      WhatsApp ile Paylaş
                    </Button>
                  </div>
                </div>
              )}

              {(portfolio.visibility === 'shared' || !shareStatus || shareStatus === 'rejected' || shareStatus === 'rejected ' || shareStatus === 'canceled' || shareStatus === 'canceled ') && (
                <div className="text-center py-4">
                  {(shareStatus === 'rejected' || shareStatus === 'rejected ') && (
                    <p className="text-red-600 font-medium mb-3 text-sm">Önceki talebiniz reddedildi. Yeniden talep edebilirsiniz.</p>
                  )}
                  {(shareStatus === 'canceled' || shareStatus === 'canceled ') && (
                    <p className="text-red-600 font-medium mb-3 text-sm">Önceki talebiniz iptal edildi veya süresi doldu. Yeniden talep edebilirsiniz.</p>
                  )}
                  <Button onClick={handleRequestShare} disabled={shareLoading}>
                    {shareLoading ? 'İşlem Yapılıyor...' : portfolio.visibility === 'shared' ? 'Özel Link Oluştur' : 'Paylaşım Talep Et'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default PortfolioDetail;
